/**
 * Timesheet Settings Types
 * Defines organisation-level and employee-level timesheet configuration
 */

export interface TimesheetDeadlineSettings {
  /**
   * Day of week when timesheets are due (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
   * Default: 5 (Friday)
   */
  deadlineDay: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  
  /**
   * Time of day when timesheets are due (24-hour format)
   * Default: '17:00' (5:00 PM)
   */
  deadlineTime: string;
  
  /**
   * Grace period in days after deadline before marking as "late"
   * Default: 2 days
   */
  gracePeriodDays: number;
  
  /**
   * Whether to show feed notifications before the submission deadline
   * Default: true
   */
  sendReminders: boolean;
  
  /**
   * Whether to allow sending email reminders from the Missing Timesheet Report
   * Default: true
   */
  sendFeedReminders: boolean;
  
  /**
   * Days before deadline to send first reminder
   * Default: 2 days
   */
  reminderDaysBefore: number;
  
  /**
   * Enable 'Copy Previous Week' button
   * Default: true
   */
  enableCopyPreviousWeek: boolean;
  
  /**
   * Include entry descriptions when copying
   * Default: true
   */
  copyDescriptions: boolean;
  
  /**
   * Copy only billable entries when copying
   * Default: false
   */
  copyOnlyBillable: boolean;
  
  /**
   * Enable auto smart fill feature
   * Default: true
   */
  enableAutoSmartFill?: boolean;
  
  /**
   * Enable AI insights feature
   * Default: true
   */
  enableAiInsights?: boolean;

  /**
   * Whether to send feed reminders to approvers when a timesheet has been
   * sitting unactioned for too long
   * Default: false
   */
  enableApprovalReminders?: boolean;

  /**
   * Number of days a timesheet must be awaiting approval before sending
   * an approval reminder to the approver (1–14)
   * Default: 2
   */
  approvalReminderDaysThreshold?: number;

  /**
   * Whether to send a weekly digest to managers listing team members
   * with missing or incomplete timesheets
   * Default: false
   */
  enableManagerDigest?: boolean;

  /**
   * Day of week to send the manager weekly digest
   * 1 = Monday, 2 = Tuesday, ..., 7 = Sunday
   * Default: 1 (Monday)
   */
  digestDayOfWeek?: 1 | 2 | 3 | 4 | 5 | 6 | 7;
}

/**
 * Working day configuration with expected hours
 */
export interface WorkingDay {
  isWorkingDay: boolean;
  expectedHours: number;
  isCustom: boolean; // Track if user manually edited
}

/**
 * Working profile - single source of truth for working hours
 */
export interface WorkingProfile {
  /**
   * Standard hours per week (configurable: 35, 37.5, 40, 48, 20, etc.)
   */
  standardHoursPerWeek: number;
  
  /**
   * Daily breakdown (auto-calculated, but can be overridden)
   */
  workingDays: {
    monday: WorkingDay;
    tuesday: WorkingDay;
    wednesday: WorkingDay;
    thursday: WorkingDay;
    friday: WorkingDay;
    saturday: WorkingDay;
    sunday: WorkingDay;
  };
  
  /**
   * Optional notes about the working pattern
   */
  notes?: string;
  
  /**
   * Effective from date
   */
  effectiveFrom?: Date;
}

/**
 * Legal/policy limits (optional - only if needed)
 */
export interface PolicyLimits {
  /**
   * Legal maximum hours per day (e.g., 13h for EU Working Time Directive)
   * Optional - only configure if you have legal requirements
   */
  legalMaxHoursPerDay?: number;
  
  /**
   * Legal maximum hours per week (e.g., 48h for EU Working Time Directive)
   * Optional - only configure if you have legal requirements
   */
  legalMaxHoursPerWeek?: number;
}

export interface UtilisationSettings {
  /**
   * Target utilisation rate (0-1, e.g., 0.75 = 75%)
   * Default: 0.75
   */
  targetUtilisationRate: number;
  
  /**
   * Utilisation calculation method
   * - 'billable-total': Billable hours / Total hours worked
   * - 'billable-available': Billable hours / Available hours (excluding PTO)
   * - 'billable-expected': Billable hours / Expected hours (from working pattern)
   * Default: 'billable-total'
   */
  calculationMethod: 'billable-total' | 'billable-available' | 'billable-expected';
}

/**
 * Hours limit settings for time tracking validation
 */
export interface HoursLimitSettings {
  /**
   * Maximum hours allowed per day
   * Default: 12
   */
  dailyHoursLimit: number;
  
  /**
   * Maximum hours allowed per week
   * Default: 48
   */
  weeklyHoursLimit: number;
  
  /**
   * Whether to enforce hard limits (block submission) vs soft limits (warning only)
   * Default: false
   */
  enforceHardLimits: boolean;
}

export type PolicyCheckType = 
  | 'daily-overage'
  | 'weekly-overage'
  | 'legal-daily-maximum'
  | 'legal-weekly-maximum'
  | 'missing-hours'
  | 'non-working-day'
  | 'daily-missing-hours'
  | 'weekly-missing-hours'
  | 'daily-non-working-day'
  | 'weekly-non-working-day';

export type PolicySeverity = 'high' | 'medium' | 'low';

export type DisplayMode = 'smart' | 'all' | 'critical-only';

export interface PolicyCheckConfig {
  enabled: boolean;
  severity: PolicySeverity;
  threshold?: number; // For overage checks (percentage over expected)
  enforcement?: 'warn' | 'block';
}

/**
 * Policy enforcement configuration - simplified version for working hours checks
 */
export interface PolicyEnforcement {
  checks: {
    DAILY_OVERAGE: PolicyCheckConfig;
    WEEKLY_OVERAGE: PolicyCheckConfig;
    LEGAL_MAXIMUM: PolicyCheckConfig;
    MISSING_HOURS: PolicyCheckConfig;
  };
  preset: 'strict' | 'balanced' | 'lenient' | 'custom';
}

export interface PolicyViolationSettings {
  /**
   * Monitoring frequency for policy checks
   * - 'day': Check hours on a daily basis
   * - 'week': Check hours on a weekly basis
   * Default: 'week'
   */
  monitoringFrequency: 'day' | 'week';
  
  /**
   * Option 1: Suppress daily warnings when weekly limit is exceeded
   * Prevents duplicate alerts for the same underlying issue
   * Default: true
   */
  suppressDailyWhenWeeklyExceeded: boolean;
  
  /**
   * Option 1: Consolidate multiple daily warnings into a single summary
   * Reduces alert fatigue when multiple days have similar issues
   * Default: true
   */
  consolidateDailyWarnings: boolean;
  
  /**
   * Option 2 & 3: Individual check configuration
   * Controls which checks are enabled and their severity
   */
  checks: Record<PolicyCheckType, PolicyCheckConfig>;
  
  /**
   * Option 3: Display mode for alerts
   * - 'smart': Apply suppression and consolidation rules
   * - 'all': Show all alerts regardless of settings
   * - 'critical-only': Show only critical severity alerts
   * Default: 'smart'
   */
  displayMode: DisplayMode;
  
  /**
   * Option 3: Whether to show info-level alerts
   * Default: true
   */
  showInfoAlerts: boolean;
  
  /**
   * Option 3: Preset configuration
   * - 'strict': All checks enabled, low thresholds
   * - 'balanced': Most checks enabled, moderate thresholds
   * - 'lenient': Fewer checks, higher thresholds
   * - 'custom': User-defined configuration
   * Default: 'balanced'
   */
  preset: 'strict' | 'balanced' | 'lenient' | 'custom';
}

// ─── Overtime Types ──────────────────────────────────────────────────────────

/**
 * A single named overtime classification configured per organisation.
 * Two independent multipliers allow different treatment of cost vs bill rates.
 */
export interface OvertimeType {
  id: number;
  name: string;
  /** Multiplier applied to the employee cost rate (e.g. 1.5). Omit to leave cost rate unaffected. */
  costMultiplier?: number;
  /** Multiplier applied to the client bill rate (e.g. 1.25). Omit to leave bill rate unaffected. */
  billMultiplier?: number;
  /** Hex colour used for the badge on the timesheet row (e.g. '#f59e0b') */
  badgeColour: string;
  /** Only one type per org can be the default — pre-selected when overtime is first flagged */
  isDefault: boolean;
  /** Soft-delete: inactive types are hidden from selection but preserved on historical entries */
  active: boolean;
  createdAt: Date;
}

/**
 * Controls when the app automatically highlights a timesheet row amber
 * to suggest it may be overtime. Highlights are passive — they never block submission.
 *
 * Working hours (daily / weekly expected) come from Resource Manager and are read via
 * the existing `policyViolations` checks on `OrganisationTimesheetSettings`. This object
 * only controls Work Type-specific highlight behaviour — not the hour values themselves.
 *
 * Highlight behaviour is driven by the existing policy checks:
 *   - `policyViolations.checks['daily-overage'].enabled`  → fire day-level highlights
 *   - `policyViolations.checks['weekly-overage'].enabled` → fire week-level highlights
 * When neither check is enabled, no threshold highlighting fires at all.
 */
export interface OvertimeThresholdSettings {
  /**
   * - 'employee-pattern': Use each person's contracted hours from Resource Manager (default)
   * - 'manual-only': No automatic highlights; employees self-declare work type only
   */
  mode: 'employee-pattern' | 'manual-only';

  /**
   * Controls which hours are counted when checking whether the threshold has been breached.
   *
   * - 'unclassified-only' (default): Only rows with no work type assigned count against the
   *   threshold. Already-classified rows are intentional overtime and are excluded — the
   *   highlight is a prompt to classify, not a permanent warning.
   *
   * - 'total-hours': All hours (normal + already-classified overtime) are summed against the
   *   threshold. Useful for orgs with strict daily/weekly hour caps regardless of
   *   pay classification (e.g. fatigue management, regulated industries).
   *   Unclassified rows in an over-threshold day are still the only rows highlighted.
   */
  thresholdScope?: 'unclassified-only' | 'total-hours';
}

/**
 * Top-level overtime configuration block stored on OrganisationTimesheetSettings.
 */
export interface OvertimeSettings {
  /** Master switch — when false all overtime UI is hidden throughout the app */
  enabled: boolean;
  thresholds: OvertimeThresholdSettings;
  /** The org's configured overtime types (empty array = no types defined yet) */
  types: OvertimeType[];
}

// ─────────────────────────────────────────────────────────────────────────────

export interface ApprovalScreenSettings {
  /**
   * Whether the issues panel opens automatically when a manager opens a timesheet for review.
   * Default: false (managers open on demand)
   */
  panelDefaultExpanded: boolean;

  /**
   * Minimum severity level to display in the approval review issues panel.
   * Issues below this threshold are hidden from managers.
   * Default: 'all'
   */
  minSeverityToShow: 'all' | 'medium' | 'high';
}

export interface OrganisationTimesheetSettings {
  /**
   * Deadline configuration
   */
  deadline: TimesheetDeadlineSettings;
  
  /**
   * Working profile - single source of truth for working hours
   */
  workingProfile: WorkingProfile;
  
  /**
   * Policy limits (optional legal/regulatory limits)
   */
  policyLimits?: PolicyLimits;
  
  /**
   * Hours limit settings for validation
   */
  hoursLimits: HoursLimitSettings;
  
  /**
   * Utilisation configuration
   */
  utilisation: UtilisationSettings;
  
  /**
   * Policy violation alert settings
   */
  policyViolations: PolicyViolationSettings;
  
  /**
   * Week start day (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
   * Default: 1 (Monday)
   */
  weekStartDay: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  
  /**
   * Whether to show and capture role on timesheet entries
   * When enabled, users can select a role for each entry (defaults from employee profile)
   * Default: false
   */
  showRole: boolean;
  
  /**
   * Whether role is required when showRole is enabled
   * Default: false
   */
  requireRole: boolean;
  
  /**
   * Whether to show and capture cost centre on timesheet entries
   * Default: true (for backward compatibility)
   */
  showCostCentre: boolean;
  
  /**
   * Whether cost centre is required when showCostCentre is enabled
   * Default: true
   */
  requireCostCentre: boolean;
  
  /**
   * Controls the granularity of descriptions on timesheet entries.
   * - 'row': One description textarea per row, covering the whole week (default)
   * - 'daily': A note input on each day cell that has hours logged
   * Default: 'row'
   */
  descriptionType: 'row' | 'daily';

  /**
   * Whether description is required on timesheet entries
   * Default: 'always'
   */
  requireDescription: 'never' | 'always';
  
  /**
   * Minimum time entry duration
   * Default: '6min'
   */
  minimumEntryDuration: '6min' | '15min' | '30min' | '1hour';
  
  /**
   * Time rounding rule applied to entered hours
   * Default: 'none'
   */
  timeRounding: 'none' | '15min' | '30min' | '1hour';
  
  /**
   * Whether negative hours are permitted for corrections
   * Default: false
   */
  allowNegativeHours: boolean;
  
  /**
   * How many weeks back employees can log time
   * Default: 4
   */
  historicalEntryLimit: number;
  
  /**
   * How far in the future employees can log time
   * Default: '2weeks'
   */
  allowFutureDatedEntries: 'no' | '1week' | '2weeks' | '4weeks' | 'unlimited';
  
  /**
   * Project selection scope- whether to show all projects or only assigned ones
   * Default: 'all'
   */
  projectSelectionScope: 'all' | 'assigned';
  
  /**
   * Whether to show archived projects in the project selector
   * Default: false
   */
  showArchivedProjects: boolean;
  
  /**
   * Whether to show remaining project time/budget in the project selector
   * Default: true
   */
  showProjectTimeRemaining: boolean;
  
  /**
   * Approval screen display settings
   * Controls how the issues panel behaves when a manager reviews a timesheet
   */
  approvalScreen: ApprovalScreenSettings;

  /**
   * Overtime tracking configuration.
   * When enabled is false, all overtime UI is hidden throughout the app.
   */
  overtime: OvertimeSettings;

  /**
   * UTC timestamp of when an administrator completed the onboarding wizard.
   * Null means onboarding has not been completed.
   */
  onboardingCompletedAt?: string | null;

  /**
   * Last updated timestamp
   */
  updatedAt?: Date;
  
  /**
   * Updated by user ID
   */
  updatedBy?: string;
}

/**
 * Default organisation settings
 */
export const DEFAULT_TIMESHEET_SETTINGS: OrganisationTimesheetSettings = {
  deadline: {
    deadlineDay: 5, // Friday
    deadlineTime: '17:00', // 5:00 PM
    gracePeriodDays: 2,
    sendReminders: true,
    sendFeedReminders: true,
    reminderDaysBefore: 2,
    enableApprovalReminders: false,
    approvalReminderDaysThreshold: 2,
    enableManagerDigest: false,
    digestDayOfWeek: 1,
    enableCopyPreviousWeek: true,
    copyDescriptions: true,
    copyOnlyBillable: false
  },
  workingProfile: {
    standardHoursPerWeek: 40,
    workingDays: {
      monday: { isWorkingDay: true, expectedHours: 8.0, isCustom: false },
      tuesday: { isWorkingDay: true, expectedHours: 8.0, isCustom: false },
      wednesday: { isWorkingDay: true, expectedHours: 8.0, isCustom: false },
      thursday: { isWorkingDay: true, expectedHours: 8.0, isCustom: false },
      friday: { isWorkingDay: true, expectedHours: 8.0, isCustom: false },
      saturday: { isWorkingDay: false, expectedHours: 0, isCustom: false },
      sunday: { isWorkingDay: false, expectedHours: 0, isCustom: false }
    }
  },
  policyLimits: {
    legalMaxHoursPerDay: 13,
    legalMaxHoursPerWeek: 48
  },
  hoursLimits: {
    dailyHoursLimit: 12,
    weeklyHoursLimit: 48,
    enforceHardLimits: false
  },
  utilisation: {
    targetUtilisationRate: 0.75,
    calculationMethod: 'billable-total'
  },
  policyViolations: {
    monitoringFrequency: 'week',
    suppressDailyWhenWeeklyExceeded: true,
    consolidateDailyWarnings: true,
    checks: {
      'daily-overage': { enabled: true, severity: 'medium', threshold: 1.25 },
      'weekly-overage': { enabled: true, severity: 'medium', threshold: 1.10 },
      'legal-daily-maximum': { enabled: false, severity: 'high' },
      'legal-weekly-maximum': { enabled: false, severity: 'high' },
      'missing-hours': { enabled: true, severity: 'medium', threshold: 0.80 },
      'non-working-day': { enabled: true, severity: 'low' },
      'daily-missing-hours': { enabled: true, severity: 'medium', threshold: 0.80 },
      'weekly-missing-hours': { enabled: true, severity: 'medium', threshold: 0.80 },
      'daily-non-working-day': { enabled: true, severity: 'low' },
      'weekly-non-working-day': { enabled: true, severity: 'low' }
    },
    displayMode: 'smart',
    showInfoAlerts: true,
    preset: 'balanced'
  },
  weekStartDay: 1, // Monday
  showRole: false, // Role capture disabled by default
  requireRole: false,
  showCostCentre: true, // Cost centre enabled by default for backward compatibility
  requireCostCentre: true,
  descriptionType: 'row', // maintains current behaviour for all existing organisations
  requireDescription: 'always',
  minimumEntryDuration: '6min',
  timeRounding: 'none',
  allowNegativeHours: false,
  historicalEntryLimit: 4,
  allowFutureDatedEntries: '2weeks',
  projectSelectionScope: 'all',
  showArchivedProjects: false,
  showProjectTimeRemaining: true,
  approvalScreen: {
    panelDefaultExpanded: false,
    minSeverityToShow: 'all',
  },
  overtime: {
    enabled: false,
    thresholds: {
      mode: 'employee-pattern',
    },
    types: [],
  },
};

/**
 * Helper to get day name from day number
 */
export function getDayName(day: number): string {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[day] || 'Unknown';
}

/**
 * Helper to get short day name from day number
 */
export function getShortDayName(day: number): string {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return days[day] || '???';
}

/**
 * Preset configurations for policy violation settings
 */
export const POLICY_PRESETS: Record<'strict' | 'balanced' | 'lenient', Omit<PolicyViolationSettings, 'preset'>> = {
  strict: {
    monitoringFrequency: 'week',
    suppressDailyWhenWeeklyExceeded: true,
    consolidateDailyWarnings: false,
    checks: {
      'daily-overage': { enabled: true, severity: 'high', threshold: 1.10 },
      'weekly-overage': { enabled: true, severity: 'high', threshold: 1.05 },
      'legal-daily-maximum': { enabled: true, severity: 'high' },
      'legal-weekly-maximum': { enabled: true, severity: 'high' },
      'missing-hours': { enabled: true, severity: 'high', threshold: 0.90 },
      'non-working-day': { enabled: true, severity: 'medium' },
      'daily-missing-hours': { enabled: true, severity: 'high', threshold: 0.90 },
      'weekly-missing-hours': { enabled: true, severity: 'high', threshold: 0.90 },
      'daily-non-working-day': { enabled: true, severity: 'medium' },
      'weekly-non-working-day': { enabled: true, severity: 'medium' }
    },
    displayMode: 'all',
    showInfoAlerts: true
  },
  balanced: {
    monitoringFrequency: 'week',
    suppressDailyWhenWeeklyExceeded: true,
    consolidateDailyWarnings: true,
    checks: {
      'daily-overage': { enabled: true, severity: 'medium', threshold: 1.25 },
      'weekly-overage': { enabled: true, severity: 'medium', threshold: 1.10 },
      'legal-daily-maximum': { enabled: false, severity: 'high' },
      'legal-weekly-maximum': { enabled: false, severity: 'high' },
      'missing-hours': { enabled: true, severity: 'medium', threshold: 0.80 },
      'non-working-day': { enabled: true, severity: 'low' },
      'daily-missing-hours': { enabled: true, severity: 'medium', threshold: 0.80 },
      'weekly-missing-hours': { enabled: true, severity: 'medium', threshold: 0.80 },
      'daily-non-working-day': { enabled: true, severity: 'low' },
      'weekly-non-working-day': { enabled: true, severity: 'low' }
    },
    displayMode: 'smart',
    showInfoAlerts: true
  },
  lenient: {
    monitoringFrequency: 'week',
    suppressDailyWhenWeeklyExceeded: true,
    consolidateDailyWarnings: true,
    checks: {
      'daily-overage': { enabled: false, severity: 'low', threshold: 1.50 },
      'weekly-overage': { enabled: true, severity: 'low', threshold: 1.20 },
      'legal-daily-maximum': { enabled: false, severity: 'high' },
      'legal-weekly-maximum': { enabled: false, severity: 'high' },
      'missing-hours': { enabled: false, severity: 'low', threshold: 0.70 },
      'non-working-day': { enabled: false, severity: 'low' },
      'daily-missing-hours': { enabled: false, severity: 'low', threshold: 0.70 },
      'weekly-missing-hours': { enabled: false, severity: 'low', threshold: 0.70 },
      'daily-non-working-day': { enabled: false, severity: 'low' },
      'weekly-non-working-day': { enabled: false, severity: 'low' }
    },
    displayMode: 'smart',
    showInfoAlerts: false
  }
};

/**
 * Get human-readable label for policy check type
 */
export function getPolicyCheckLabel(type: PolicyCheckType): string {
  const labels: Record<string, string> = {
    'daily-overage': 'Daily Hours Over Expected',
    'weekly-overage': 'Weekly Hours Over Expected',
    'legal-daily-maximum': 'Legal Daily Maximum Exceeded',
    'legal-weekly-maximum': 'Legal Weekly Maximum Exceeded',
    'missing-hours': 'Missing Expected Hours',
    'non-working-day': 'Work on Non-Working Day',
    'daily-missing-hours': 'Daily Missing Hours',
    'weekly-missing-hours': 'Weekly Missing Hours',
    'daily-non-working-day': 'Daily Non-Working Day Entry',
    'weekly-non-working-day': 'Weekly Non-Working Day Entry'
  };
  return labels[type] ?? type;
}

/**
 * Get description for policy check type
 */
export function getPolicyCheckDescription(type: PolicyCheckType): string {
  const descriptions: Record<string, string> = {
    'daily-overage': 'Alerts when daily hours exceed expected hours by threshold percentage (e.g., 125% of 8h = 10h)',
    'weekly-overage': 'Alerts when weekly hours exceed expected hours by threshold percentage (e.g., 110% of 40h = 44h)',
    'legal-daily-maximum': 'Alerts when daily hours exceed legal maximum (e.g., 13h for EU Working Time Directive)',
    'legal-weekly-maximum': 'Alerts when weekly hours exceed legal maximum (e.g., 48h for EU Working Time Directive)',
    'missing-hours': 'Alerts when hours are below expected for the period (e.g., below 80% of expected)',
    'non-working-day': 'Alerts when hours are logged on non-working days (weekends, holidays)',
    'daily-missing-hours': 'Alerts when daily hours are below expected for the day',
    'weekly-missing-hours': 'Alerts when weekly hours are below expected for the week',
    'daily-non-working-day': 'Alerts when hours are logged on non-working days (daily check)',
    'weekly-non-working-day': 'Alerts when hours are logged on non-working days (weekly check)'
  };
  return descriptions[type] ?? type;
}

/**
 * Get severity badge color
 */
export function getSeverityColor(severity: PolicySeverity): string {
  const colors: Record<PolicySeverity, string> = {
    high: 'destructive',
    medium: 'warning',
    low: 'secondary'
  };
  return colors[severity];
}

/**
 * Get display mode description
 */
export function getDisplayModeDescription(mode: DisplayMode): string {
  const descriptions: Record<DisplayMode, string> = {
    smart: 'Apply suppression and consolidation rules to reduce alert fatigue',
    all: 'Show all alerts regardless of suppression settings',
    'critical-only': 'Show only high severity alerts'
  };
  return descriptions[mode];
}
